/**
 * Client-side validation for supported video URL imports.
 * Keep in sync with automationtool/src/helpers/video_url_helper.py
 */

const BLOCKED_HOST_PATTERNS = [
  /(^|\.)tiktok\.com$/i,
  /(^|\.)instagram\.com$/i,
];

const ALLOWED_HOST_PATTERNS = [
  /(^|\.)youtube\.com$/i,
  /(^|\.)youtu\.be$/i,
  /(^|\.)drive\.google\.com$/i,
  /(^|\.)vimeo\.com$/i,
  /(^|\.)twitch\.tv$/i,
  /(^|\.)facebook\.com$/i,
  /(^|\.)fb\.watch$/i,
  /(^|\.)fb\.com$/i,
  /(^|\.)linkedin\.com$/i,
  /(^|\.)twitter\.com$/i,
  /(^|\.)x\.com$/i,
  /(^|\.)reddit\.com$/i,
  /(^|\.)v\.redd\.it$/i,
  /(^|\.)zoom\.us$/i,
];

const DIRECT_VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.mkv', '.m4v', '.avi', '.wmv'];

function getHostname(url) {
  try {
    return new URL(url.trim()).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function isDirectVideoUrl(url) {
  try {
    const path = new URL(url.trim()).pathname.toLowerCase();
    return DIRECT_VIDEO_EXTENSIONS.some((ext) => path.endsWith(ext));
  } catch {
    return false;
  }
}

export function isSupportedVideoUrl(url) {
  if (!url || !url.trim()) return false;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;

  const host = getHostname(trimmed);
  if (!host) return false;

  if (BLOCKED_HOST_PATTERNS.some((p) => p.test(host))) return false;
  if (ALLOWED_HOST_PATTERNS.some((p) => p.test(host))) return true;
  return isDirectVideoUrl(trimmed);
}

/**
 * @returns {{ isValid: boolean, error: string }}
 */
export function validateVideoUrl(url) {
  if (!url || !url.trim()) {
    return { isValid: false, error: '' };
  }

  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return { isValid: false, error: 'URL must start with http:// or https://' };
  }

  const host = getHostname(trimmed);
  if (!host) {
    return { isValid: false, error: 'Invalid URL' };
  }

  if (BLOCKED_HOST_PATTERNS.some((p) => p.test(host))) {
    return { isValid: false, error: 'This platform is not supported. Upload the file instead.' };
  }

  if (ALLOWED_HOST_PATTERNS.some((p) => p.test(host)) || isDirectVideoUrl(trimmed)) {
    return { isValid: true, error: '' };
  }

  return {
    isValid: false,
    error: 'Please enter a supported video URL (YouTube, Drive, Vimeo, X, and more)',
  };
}
